import Avatar from "components/core/Avatar";
import type {
  IAmericanCompanyIssuerHoverCardFieldsFragment,
  IBrazilianCompanyIssuerHoverCardFieldsFragment,
  IBrazilianRealEstateFundHoverCardFieldsFragment,
} from "components/issuers/graphql/fragments/__generated__/IssuerHoverCardFields.generated";
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from "components/ui/HoverCard";
import classNames from "components/ui/classNames";
import type { ReactNode } from "react";

interface IIssuerHoverCardProps {
  issuer:
    | IBrazilianCompanyIssuerHoverCardFieldsFragment
    | IAmericanCompanyIssuerHoverCardFieldsFragment
    | IBrazilianRealEstateFundHoverCardFieldsFragment;
  children: ReactNode;
}

export default function IssuerHoverCard({ issuer, children }: IIssuerHoverCardProps) {
  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild={true}>{children}</HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent side="top" sideOffset={12} className="w-96">
          {issuer.__typename === "AmericanCompanyIssuer" && (
            <div className="relative">
              <div className="space-y-2">
                <h4 className="text-lg font-semibold">{issuer.name}</h4>
                <div className="space-x-2">
                  <h6
                    className={classNames(
                      "inline-flex rounded-md px-2 py-1 text-xs font-bold",
                      "bg-violet-50 text-violet-600",
                      "dark:bg-orange-300/30 dark:text-orange-100",
                    )}
                  >
                    {issuer.primaryTickerSymbol}
                  </h6>
                  {issuer.isAmericanDepositaryReceipt && (
                    <h6 className="inline-flex rounded-md bg-violet-100 px-2 py-1 text-xs font-bold text-violet-600">
                      ADR
                    </h6>
                  )}
                </div>
                <span className="flex text-sm font-medium italic text-gray-600 dark:text-gray-500">
                  {issuer.sector}
                </span>
                <p className="line-clamp-3 text-sm dark:text-gray-400 text-gray-500 tracking-wider">
                  {issuer.businessDescription}
                </p>
              </div>

              <div className="absolute right-0 top-0">
                <Avatar alt={issuer.name} src={issuer.niceLogo?.url || null} size={44} squircle={true} />
              </div>
            </div>
          )}

          {issuer.__typename === "BrazilianCompanyIssuer" && (
            <div className="relative">
              <div className="space-y-2">
                <h4 className="text-lg font-semibold">{issuer.name}</h4>
                <div className="space-x-2">
                  <h6
                    className={classNames(
                      "inline-flex rounded-md px-2 py-1 text-xs font-bold",
                      "bg-violet-50 text-violet-600",
                      "dark:bg-orange-300/30 dark:text-orange-100",
                    )}
                  >
                    {issuer.tickerSymbolPrefix}
                  </h6>
                  {issuer.isBrazilianDepositaryReceipt && (
                    <h6 className="inline-flex rounded-md bg-violet-100 px-2 py-1 text-xs font-bold text-violet-700">
                      BDR
                    </h6>
                  )}
                </div>
                <span className="flex text-sm font-medium italic text-gray-600 dark:text-gray-500">
                  {issuer.sector}
                </span>
                <p className="line-clamp-3 text-sm dark:text-gray-400 text-gray-500 tracking-wider">
                  {issuer.businessDescription}
                </p>
              </div>

              <div className="absolute right-0 top-0">
                <Avatar alt={issuer.name} src={issuer.niceLogo?.url || null} size={44} squircle={true} />
              </div>
            </div>
          )}

          {issuer.__typename === "BrazilianRealEstateFund" && (
            <div className="relative">
              <div className="space-y-2">
                <h4 className="text-lg font-semibold">{issuer.name}</h4>
                <div className="space-x-2">
                  <h6
                    className={classNames(
                      "inline-flex rounded-md px-2 py-1 text-xs font-bold",
                      "bg-violet-50 text-violet-600",
                      "dark:bg-orange-300/30 dark:text-orange-100",
                    )}
                  >
                    {issuer.tickerSymbol}
                  </h6>
                </div>
                <span className="flex text-sm font-medium italic text-gray-600 dark:text-gray-500">
                  {issuer.selfRegulation.primarySegment}
                </span>
                <p className="line-clamp-3 text-sm text-gray-500 dark:text-gray-400 tracking-wider">
                  {issuer.shortDescription}
                </p>
              </div>
            </div>
          )}
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}
